/**
 * Funnel Analytics Service
 * Tracks user progression through the productivity goals funnel
 */

export type FunnelStep =
  | 'landing_viewed'
  | 'goals_submitted'
  | 'questions_started'
  | 'question_1'
  | 'question_2'
  | 'question_3'
  | 'question_4'
  | 'question_5'
  | 'questions_completed'
  | 'email_viewed'
  | 'email_submitted'
  | 'report_viewed'
  | 'converted_to_full_user';

interface FunnelEvent {
  step: FunnelStep;
  softUserId: string;
  email?: string;
  goalCount?: number;
  questionCount?: number;
  timestamp: string;
  sessionId: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

class FunnelAnalytics {
  private apiBaseUrl: string;
  private sessionId: string;
  private softUserId: string;

  constructor() {
    this.apiBaseUrl = import.meta.env.VITE_API_URL || 'https://mybestday.ai/api';
    this.sessionId = this.getOrCreateSessionId();
    this.softUserId = this.getOrCreateSoftUserId();
  }

  /**
   * Get or create session ID
   */
  private getOrCreateSessionId(): string {
    const key = 'mbd_funnel_session';
    let sessionId = sessionStorage.getItem(key);

    if (!sessionId) {
      sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      sessionStorage.setItem(key, sessionId);
    }

    return sessionId;
  }

  /**
   * Get or create soft user ID (persists across sessions)
   */
  private getOrCreateSoftUserId(): string {
    const key = 'mbd_soft_user_id';
    let userId = localStorage.getItem(key);

    if (!userId) {
      userId = `soft_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      localStorage.setItem(key, userId);
    }

    return userId;
  }

  /**
   * Get UTM parameters from URL
   */
  private getUtmParams(): { utmSource?: string; utmMedium?: string; utmCampaign?: string } {
    const params = new URLSearchParams(window.location.search);
    return {
      utmSource: params.get('utm_source') || undefined,
      utmMedium: params.get('utm_medium') || undefined,
      utmCampaign: params.get('utm_campaign') || undefined,
    };
  }

  /**
   * Track a funnel step
   */
  async trackStep(step: FunnelStep, metadata?: {
    email?: string;
    goalCount?: number;
    questionCount?: number;
  }) {
    const event: FunnelEvent = {
      step,
      softUserId: this.softUserId,
      email: metadata?.email,
      goalCount: metadata?.goalCount,
      questionCount: metadata?.questionCount,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      referrer: document.referrer || undefined,
      ...this.getUtmParams(),
    };

    try {
      await fetch(`${this.apiBaseUrl}/analytics/funnel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
      });

      console.log('[Funnel Analytics] Tracked:', step, metadata);
    } catch (error) {
      console.error('[Funnel Analytics] Failed to track:', error);
    }
  }

  /**
   * Get soft user ID for linking with full user account
   */
  getSoftUserId(): string {
    return this.softUserId;
  }

  /**
   * Track landing page view
   */
  trackLandingViewed() {
    this.trackStep('landing_viewed');
  }

  /**
   * Track goals submission
   */
  trackGoalsSubmitted(goalCount: number) {
    this.trackStep('goals_submitted', { goalCount });
  }

  /**
   * Track questions started
   */
  trackQuestionsStarted(questionCount: number) {
    this.trackStep('questions_started', { questionCount });
  }

  /**
   * Track individual question answered
   */
  trackQuestionAnswered(questionNumber: number) {
    const step = `question_${questionNumber}` as FunnelStep;
    this.trackStep(step, { questionCount: questionNumber });
  }

  /**
   * Track questions completed
   */
  trackQuestionsCompleted(questionCount: number) {
    this.trackStep('questions_completed', { questionCount });
  }

  /**
   * Track email capture page viewed
   */
  trackEmailViewed() {
    this.trackStep('email_viewed');
  }

  /**
   * Track email submitted
   */
  trackEmailSubmitted(email: string) {
    this.trackStep('email_submitted', { email });
  }

  /**
   * Track report viewed
   */
  trackReportViewed() {
    this.trackStep('report_viewed');
  }

  /**
   * Track conversion to full user
   */
  trackConvertedToFullUser(email: string) {
    this.trackStep('converted_to_full_user', { email });
  }
}

// Create singleton instance
export const funnelAnalytics = new FunnelAnalytics();

export default funnelAnalytics;
